import React, { useState, useEffect, useRef } from 'react';
import { SearchCoreProps } from './SearchCoreModal';
import { UserClass, SetLoading } from '../SiteClasses';
import { Button, Header, Input, Pagination, Modal, Table, Icon } from 'react-frontier';
import { Dropdown } from 'semantic-ui-react';
import API from '../API';

interface SearchUserModalProps extends SearchCoreProps<UserClass>{
	defaultTag?: number | string | string[];
	tagName?: string;
	tagOptions?: Array<{tag: string, label: string}>;
}

var SearchUserModal = (props: SearchUserModalProps)=>{
	var [filterTag, setFilterTag] = useState<boolean>(false);
	var [resolvedTagIds, setResolvedTagIds] = useState<Array<{tagId: number, tag: string, label: string}>>(null);
	var [selectedTagId, setSelectedTagId] = useState<number>(null);
	var [searching, setSearching] = useState<boolean>(false);
	var [searchInput, setSearchInput] = useState<string>('');
	var [results, setResults] = useState<any[]>(null);
	var [resultsError, setResultsError] = useState<string>(null);
	var [searchResults, setSearchResults] = useState<any[]>(null);
	var [searchError, setSearchError] = useState<string>(null);
	var [sentQuery, setSentQuery] = useState<string>(null);
	var [page, setPage] = useState<number>(0);
	var [loading, setLoading] = useState<boolean>(false);
	var inputRef = useRef<HTMLInputElement>(null);
	var PAGE_SIZE = (props.pageSize || 5);

	useEffect(()=>{
		if(!props.open) return;

		setSearchInput('');
		inputRef.current?.focus();
		setSearching(true);
		setPage(0);
		setSearchError(null);
		setResultsError(null);
		setSearchResults(null);

		var resolveTags = async ()=>{
			if(!props.tagOptions) {
				if(typeof props.defaultTag === 'number') {
					return [{tagId: props.defaultTag, tag: '', label: props.tagName || ''}];
				}
				if(!props.defaultTag) return null;
				
				var res = await API.getAllTags();
				if(res.error) return null;
				
				var search = Array.isArray(props.defaultTag) ? props.defaultTag : [props.defaultTag];
				for(var s of search){
					var t = res.data.find(t=>t.tag_name.toLowerCase().includes(s.toLowerCase()));
					if(t) return [{tagId: t.tag_id, tag: s, label: s}];
				}
				return null;
			}
			
			var res = await API.getAllTags();
			if(res.error) return null;
			
			var resolved = props.tagOptions
				.map(option=>{
					var t = res.data.find(t=>t.tag_name.toLowerCase().includes(option.tag.toLowerCase()));
					return t ? {tagId: t.tag_id, tag: option.tag, label: option.label} : null;
				})
				.filter(Boolean);
			
			return resolved.length > 0 ? resolved : null;
		};

		resolveTags().then(tagIds=>{
			setResolvedTagIds(tagIds);
			var hasTag = !!tagIds?.length;
			setFilterTag(hasTag);
			setSelectedTagId(hasTag ? tagIds[0].tagId : null);
			
			API.searchUsers('', 0, PAGE_SIZE, hasTag ? tagIds[0].tagId : null)
				.then(res=>{
					if(res.error) return setSearchError(res.message);
					setSearchResults(res.data);
				})
				.catch(()=>setSearchError('Hubo un error inesperado cargando los datos (LCL-SCHCRE-1)'))
				.finally(()=>setSearching(false));
		});
	}, [props.open]);

	var getIndexes = (arr: any[])=>arr.map((val, i)=>val ? i : -1).filter(i=>i >= 0);

	var searchData = (setLoading: SetLoading, page: number = 0, forceFilterTag?: boolean, forceTagId?: number)=>{
		if(searching) return;
		
		var useFilter = forceFilterTag ?? filterTag;
		var useTagId = forceTagId ?? selectedTagId;
		var pageNum = page || 0;
		
		setSearching(true);
		setSentQuery(searchInput);
		setPage(pageNum);
		setSearchError(null);
		setResultsError(null);
		
		API.searchUsers(searchInput, pageNum * PAGE_SIZE, PAGE_SIZE, useFilter ? useTagId : null)
			.then(res=>{
				if(res.error) return setSearchError(res.message);
				setSearchResults(res.data);
			})
			.catch(()=>setSearchError('Hubo un error inesperado cargando los datos (LCL-SCHCRE-1)'))
			.finally(()=>setSearching(false));
	}

	var itemSelected = (item: any)=>()=>props.onSelected?.(item, setLoading);

	var onPageChange = (page: number)=>{
		setPage(page);
		searchData(null, page);
	}

	var hasPagination = (searchResults?.length || 0) >= PAGE_SIZE || page !== 0;
	var shown_results = searchResults || results;

	var table: any[] = [
		{ name: 'ID', key: 'user_id', collapsing: true },
		{ name: 'Nombre', key: 'first_name', collapsing: true },
		{ name: 'Apellido', key: 'last_name', collapsing: true },
		{ name: 'Email', key: 'email', collapsing: true },
	];

	return <Modal open={props.open} onClose={props.onClose} size={props.size || 'small'}>
		<Modal.Header>{props.title || 'Buscar usuario'}</Modal.Header>
		<Modal.Content style={{ paddingBottom: hasPagination ? 0 : 20, overflow: 'hidden', maxHeight: 500 }}>
			{!!props.open && (
				<Input 
					ref={inputRef} 
					value={searchInput} 
					onChange={setSearchInput} 
					placeholder={props.searchPlaceholder || 'Buscar registros'} 
					inputType='text' 
					onSubmit={searchData} 
					submitOnEnter 
					button={
						<div style={{ display: 'flex', gap: 0 }}>
							{resolvedTagIds && resolvedTagIds.length > 0 && (
								<Dropdown 
									icon='filter' 
									button 
									className='icon'
									style={{ 
										borderTopRightRadius: 0, 
										borderBottomRightRadius: 0,
										marginRight: -1
									}}
								>
									<Dropdown.Menu>
										{resolvedTagIds.length === 1 ? (
											<Dropdown.Item onClick={()=>{
												var newFilterTag = !filterTag;
												setFilterTag(newFilterTag);
												searchData(null, 0, newFilterTag, resolvedTagIds[0].tagId);
											}}>
												<i className={`icon circle ${filterTag ? 'check' : 'outline'}`} />
												<span>{(props.tagName || resolvedTagIds[0].label).toUpperCase()}</span>
											</Dropdown.Item>
										) : (
											<>
												<Dropdown.Item onClick={()=>{
													setFilterTag(false);
													setSelectedTagId(null);
													searchData(null, 0, false, null);
												}}>
													<i className={`icon circle ${!filterTag ? 'check' : 'outline'}`} />
													<span>TODOS</span>
												</Dropdown.Item>
												<Dropdown.Divider />
												{resolvedTagIds.map((tagOption, idx)=>(
													<Dropdown.Item 
														key={`tag-${idx}`}
														onClick={()=>{
															setSelectedTagId(tagOption.tagId);
															setFilterTag(true);
															searchData(null, 0, true, tagOption.tagId);
														}}
													>
														<i className={`icon circle ${selectedTagId === tagOption.tagId && filterTag ? 'check' : 'outline'}`} />
														<span>{tagOption.label.toUpperCase()}</span>
													</Dropdown.Item>
												))}
											</>
										)}
									</Dropdown.Menu>
								</Dropdown>
							)}
							<Button 
								text='Buscar' 
								onClick={searchData} 
								loading={searching}
								style={resolvedTagIds && resolvedTagIds.length > 0 ? {
									borderTopLeftRadius: 0,
									borderBottomLeftRadius: 0
								} : {}}
							/>
						</div>
					}
				/>
			)}
			{searching ? (
				<Header loading text="Cargando registros" containerStyle={{ marginBottom: 50, marginTop: 50 }} />
			) : (searchError || resultsError) ? (
				<Header text="Error" subtext={searchError || resultsError} iconName="face-frown-open" style={{ marginTop: 30, marginBottom: 30 }} size="small" />
			) : !shown_results ? null : (shown_results.length === 0 && page <= 0) ? (
				<Header 
					text="Sin resultados" 
					subtext={`No se encontraron registros${sentQuery ? ` con "${sentQuery}"` : ''}`} 
					iconName="face-frown-open" 
					style={{ marginTop: 50, marginBottom: 30 }} 
					size="small" 
				/>
			) : <>
				<div style={{ position: 'relative' }}>
					<Table fitted selectable striped divided
						style={{ marginTop: 20 }}
						className='first bottom round'
						headers={table.map(a=>a.name)}
						centeredIndexes={getIndexes(table.map(a=>a.centered))}
						collapsingIndexes={getIndexes(table.map(a=>a.collapsing))}
						footer={hasPagination && (
							<Table.Cell row colSpan={100}>
								<div style={{ justifyContent: 'flex-end', display: 'flex', marginBottom: 15 }}>
									<Pagination page={page} disabled={searching} onPageChange={onPageChange} hasNext={shown_results.length >= PAGE_SIZE}/>
								</div>
							</Table.Cell>
						)}
					>
						{(shown_results.length === 0) ? (
							<Table.Cell row colSpan={100}>
								<Header text='Sin resultados' subtext={`Esta página no tiene resultados`} iconName='face-frown-open' style={{ marginTop: 30, marginBottom: 30 }} size='small' />
							</Table.Cell>
						) : (
							shown_results.map((a, i)=>(
								<Table.Row key={`SRCH-CRTR-${i}`} onClick={itemSelected(a)}>
									{table.map(t=>(
										<Table.Cell collapsing={t.collapsing} centered={t.centered} key={`SRCH-CRTD-${i}-${t.key as string}`}>
											{t.render ? t.render(a[t.key], a) : a[t.key]}
										</Table.Cell>
									))}
								</Table.Row>
							))
						)}
					</Table>
				</div>
			</>}
			{loading && (
				<div style={{ position: 'absolute', width: '100%', left: 0, top: 0, height: '100%', backgroundColor: 'rgba(0, 0, 0, 0.5)', borderRadius: 8 }} className='fr big loading'></div>
			)}
		</Modal.Content>
	</Modal>
}

export default SearchUserModal;